import {
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import {
  LandingPage,
  AuthCallbackPage,
  ResetPasswordPage,
  DashboardPage,
  BillingPage,
  PlanSelectionPage,
  CompletePaymentPage,
  PlanChangePage,
  SettingsPage,
  PersonalCategoriesPage,
  BudgetManagementPage,
  SavingsGoalsPage,
  PersonalTransactionsPage,
  PersonalDashboardPage,
  BudgetReportsPage,
  PersonalLoansPage,
} from '@/pages';
import { AuthGate } from './AuthGate';
import { Toaster } from '@/components/ui/toaster';

// Root layout component with toaster
const RootComponent = () => {
  return (
    <>
      <Outlet />
      <Toaster />
    </>
  );
};

// Create root route
const rootRoute = createRootRoute({
  component: RootComponent,
});

// ============================================
// PUBLIC ROUTES (no auth required)
// ============================================

// Landing page (/)
const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

// Signup route (/signup) - redirects to landing with modal flag
// Marketing site links here with ?plan= param
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  beforeLoad: ({ search }) => {
    const searchParams = search as { plan?: string };
    // Store plan in sessionStorage if present
    if (searchParams.plan && ['solo', 'pro', 'team'].includes(searchParams.plan)) {
      sessionStorage.setItem('sapling-selected-plan', searchParams.plan);
    }
    // Set flag to auto-open signup modal
    sessionStorage.setItem('sapling-open-signup', 'true');
    // Redirect to landing page (modal will auto-open)
    throw redirect({ to: '/' });
  },
});

// Reset password page (/reset-password)
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});

// Auth callback page (/auth/callback)
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
});

// ============================================
// PROTECTED ROUTES (auth required)
// ============================================

// App layout (parent for all authenticated routes)
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: AuthGate,
});

// Dashboard (/app/dashboard)
const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/dashboard',
  component: DashboardPage,
});

// Complete payment (/app/complete-payment)
const completePaymentRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/complete-payment',
  component: CompletePaymentPage,
});

// Billing management (/app/billing)
const billingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/billing',
  component: BillingPage,
});

// Plan selection (/app/plan-selection)
const planSelectionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/plan-selection',
  component: PlanSelectionPage,
});

// Plan change confirmation (/app/plan-change)
const planChangeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/plan-change',
  component: PlanChangePage,
});

// Settings (/app/settings)
const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});

// Personal Categories (/app/personal/categories)
const personalCategoriesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/categories',
  component: PersonalCategoriesPage,
});

// Budget Management (/app/personal/budgets)
const personalBudgetsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/budgets',
  component: BudgetManagementPage,
});

// Savings Goals (/app/personal/savings-goals)
const savingsGoalsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/savings-goals',
  component: SavingsGoalsPage,
});

// Personal Dashboard (/app/personal/dashboard)
const personalDashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/dashboard',
  component: PersonalDashboardPage,
});

// Budget Reports (/app/personal/reports)
const budgetReportsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/reports',
  component: BudgetReportsPage,
});

// Personal Loans (/app/personal/loans)
const personalLoansRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/loans',
  component: PersonalLoansPage,
});

// Personal Transactions (/app/personal/transactions)
const personalTransactionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/personal/transactions',
  component: PersonalTransactionsPage,
});

// App index route (redirects to dashboard)
const appIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/app/dashboard' });
  },
});

// ============================================
// ROUTE TREE
// ============================================

const routeTree = rootRoute.addChildren([
  landingRoute,
  signupRoute,
  resetPasswordRoute,
  authCallbackRoute,
  appLayoutRoute.addChildren([
    appIndexRoute,
    dashboardRoute,
    billingRoute,
    completePaymentRoute,
    planSelectionRoute,
    planChangeRoute,
    settingsRoute,
    personalDashboardRoute,
    personalCategoriesRoute,
    personalBudgetsRoute,
    savingsGoalsRoute,
    personalTransactionsRoute,
    personalLoansRoute,
    budgetReportsRoute,
  ]),
]);

// ============================================
// ROUTER INSTANCE
// ============================================

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
